import { FileActionButton } from "../file-action/file-action-controls";
import React, { useEffect, useState } from "react";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import ReportPageHeader from "../shared/ReportPageHeader";
import {
  CustomerExecutiveSalesReport1,
  CustomerExecutiveSalesReport2,
  Response,
  CustomerDetails,
  RequestInput,
  CustomerSalesReportResponse,
} from "../../services/edit-users/edit-users.service";
import { exportReport1Excel, exportReport2Excel } from "./CESalesExport";

const SalesReportTables: React.FC = () => {
  // Date filters
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  // Left Table (Report1) - Client-side
  const [allNewUsers, setAllNewUsers] = useState<Response[]>([]);
  const [leftFirst, setLeftFirst] = useState(0);
  const [leftRows, setLeftRows] = useState(10);
  const [searchReport1, setSearchReport1] = useState("");

  // Right Table (Report2) - Server-side
  const [allCustomers, setAllCustomers] = useState<CustomerDetails[]>([]);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [rightFirst, setRightFirst] = useState(0);
  const [rightRows, setRightRows] = useState(10);
  const [searchReport2, setSearchReport2] = useState("");

  // Loading states
  const [loadingReport1, setLoadingReport1] = useState(false);
  const [loadingReport2, setLoadingReport2] = useState(false);

  // FIXED: Preserve local date instead of converting to UTC
  const formatDate = (date: Date | null): string =>
    date
      ? `${date.getFullYear()}-${(date.getMonth() + 1)
          .toString()
          .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`
      : "";

  // Fetch Report 1
  const fetchReport1 = async () => {
    if (!startDate || !endDate) return;
    setLoadingReport1(true);

    const filter: RequestInput = {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
      pageNumber: 1,
      pageSize: 10000,
    };

    try {
      const report1 = await CustomerExecutiveSalesReport1(filter);
      setAllNewUsers(report1);
    } catch (error) {
      console.error("Error fetching report 1:", error);
    } finally {
      setLoadingReport1(false);
    }
  };

  // Fetch Report 2
  const fetchReport2 = async (pageNumber: number = 1) => {
    if (!startDate || !endDate) return;
    setLoadingReport2(true);

    const filter: RequestInput = {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
      pageNumber,
      pageSize: rightRows,
    };

    try {
      const response: CustomerSalesReportResponse =
        await CustomerExecutiveSalesReport2(filter);
      setAllCustomers(response.salesReports);
      setTotalCustomers(response.totalRecords);
    } catch (error) {
      console.error("Error fetching report 2:", error);
    } finally {
      setLoadingReport2(false);
    }
  };

  // Auto-fetch when dates change
  useEffect(() => {
    setLeftFirst(0);
    setRightFirst(0);
    fetchReport1();
    fetchReport2(1);
  }, [startDate, endDate, rightRows]);

  // Pagination handlers
  const onReport1PageChange = (e: DataTableStateEvent) => {
    setLeftFirst(e.first);
    setLeftRows(e.rows);
  };

  const onReport2PageChange = (e: DataTableStateEvent) => {
    const newPage = Math.floor(e.first / e.rows) + 1;
    setRightFirst(e.first);
    setRightRows(e.rows);
    fetchReport2(newPage);
  };

  const handleFilter = () => {
    setLeftFirst(0);
    setRightFirst(0);
    fetchReport1();
    fetchReport2(1);
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setSearchReport1("");
    setSearchReport2("");
    setAllNewUsers([]);
    setAllCustomers([]);
    setTotalCustomers(0);
    setLeftFirst(0);
    setRightFirst(0);
  };

  const formatRupees = (amount?: number) =>
    amount !== undefined
      ? new Intl.NumberFormat("en-IN", {
          style: "currency",
          currency: "INR",
        }).format(amount)
      : "₹0.00";

  // Apply filtering for Report 1 (client-side)
  const filteredReport1 = allNewUsers.filter((user) =>
    user.customerExecutive?.toLowerCase().includes(searchReport1.toLowerCase())
  );
  const paginatedReport1 = filteredReport1.slice(
    leftFirst,
    leftFirst + leftRows
  );

  // Apply filtering for Report 2 (client-side)
  const filteredReport2 = allCustomers.filter(
    (cust) =>
      cust.customerExecutive?.toLowerCase().includes(searchReport2.toLowerCase()) ||
      cust.customerName?.toLowerCase().includes(searchReport2.toLowerCase())
  );

  return (
    <div className="p-6">
      <ReportPageHeader
        icon="pi-chart-bar"
        title="Customer Executives Sales Report"
        subtitle="Compare executive sales totals and customer-level invoicing performance"
        filterLabel="Filter Sales Reports"
      >
        <div className="grid">
          <div className="col-12 md:col-4">
            <Calendar
              value={startDate}
              onChange={(e) => setStartDate(e.value as Date)}
              dateFormat="dd/mm/yy"
              placeholder="Start Date"
              className="w-full"
              readOnlyInput
            />
          </div>
          <div className="col-12 md:col-4">
            <Calendar
              value={endDate}
              onChange={(e) => setEndDate(e.value as Date)}
              dateFormat="dd/mm/yy"
              placeholder="End Date"
              className="w-full"
              readOnlyInput
            />
          </div>
          <div className="col-6 md:col-2">
            <Button
              label="Filter"
              onClick={handleFilter}
              severity="success"
              className="w-full"
              disabled={!startDate || !endDate}
            />
          </div>
          <div className="col-6 md:col-2">
            <Button
              label="Reset"
              onClick={handleReset}
              text
              raised
              className="w-full"
            />
          </div>
        </div>
      </ReportPageHeader>

      {/* Report 1 */}
      <div className="card p-4 shadow-md rounded-lg border border-gray-400 bg-white text-black mb-6">
        <div className="flex justify-between items-center mb-3 w-full">
          {/* Left: Title + Search */}
          <div className="flex items-center space-x-3">
            <h3 className="font-semibold text-lg text-black">Sales Report-1</h3>
            <span className="ml-4"></span>
            <InputText
              placeholder="Search Name..."
              value={searchReport1}
              onChange={(e) => setSearchReport1(e.target.value)}
              className="p-inputtext-sm w-64"
            />
          </div>

          {/* Right: Export */}
          <FileActionButton action="export"
            label="Export"
            icon="pi pi-download"
            className="p-button-outlined px-5 py-2 ml-auto"
            onClick={async () => {
              const filter: RequestInput = {
                startDate: formatDate(startDate),
                endDate: formatDate(endDate),
                pageNumber: 1,
                pageSize: 10000,
              };
              const report1 = await CustomerExecutiveSalesReport1(filter);
              exportReport1Excel(report1);
            }}
            disabled={allNewUsers.length === 0}
          />
        </div>

        <DataTable
          value={paginatedReport1}
          paginator
          rows={leftRows}
          first={leftFirst}
          totalRecords={filteredReport1.length}
          onPage={onReport1PageChange}
          loading={loadingReport1}
          rowsPerPageOptions={[5, 10, 25, 50]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PageLinks CurrentPageReport"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          className="text-black border border-gray-300"
          emptyMessage="No records found."
        >
          <Column field="customerExecutive" header="Executive" sortable />
          <Column
            field="taxableAmount"
            header="Taxable Amount"
            sortable
            body={(row: Response) => formatRupees(row.taxableAmount)}
          />
        </DataTable>
      </div>

      {/* Report 2 */}
      <div className="card p-4 shadow-md rounded-lg border border-gray-400 bg-white text-black mb-6">
        <div className="flex justify-between items-center mb-3 w-full">
          {/* Left: Title + Search */}
          <div className="flex items-center space-x-3">
            <h3 className="font-semibold text-lg text-black">Sales Report-2</h3>
            <span className="ml-4"></span>
            <InputText
              placeholder="Search Name..."
              value={searchReport2}
              onChange={(e) => setSearchReport2(e.target.value)}
              className="p-inputtext-sm w-64"
            />
          </div>

          {/* Right: Export */}
          <FileActionButton action="export"
            label="Export"
            icon="pi pi-download"
            className="p-button-outlined px-5 py-2 ml-auto"
            onClick={async () => {
              const filter: RequestInput = {
                startDate: formatDate(startDate),
                endDate: formatDate(endDate),
                pageNumber: 1,
                pageSize: 10000,
              };
              const report2Response = await CustomerExecutiveSalesReport2(filter);
              exportReport2Excel(report2Response.salesReports);
            }}
            disabled={allCustomers.length === 0}
          />
        </div>

        <DataTable
          value={filteredReport2}
          paginator
          rows={rightRows}
          first={rightFirst}
          totalRecords={filteredReport2.length}
          onPage={onReport2PageChange}
          loading={loadingReport2}
          rowsPerPageOptions={[5, 10, 25, 50]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          className="text-black border border-gray-300"
          emptyMessage="No records found."
        >
          <Column field="customerExecutive" header="Executive" sortable />
          <Column field="customerName" header="Customer Name" sortable />
          <Column field="invoicedBy" header="InvoicedBy" sortable />
          <Column field="customerNumb" header="Contact" sortable />
          <Column
            field="taxableAmount"
            header="Taxable Amount"
            sortable
            body={(row: CustomerDetails) => formatRupees(row.taxableAmount)}
          />
        </DataTable>
      </div>
    </div>
  );
};

export default SalesReportTables;
